import { put, take, select, fork, call } from 'redux-saga/effects';
import { takeLatest, takeEvery, delay } from 'redux-saga';

import * as actions from 'actions';
import * as api from 'api';
import { getCommentById } from 'selectors';
import { optimisticUpdateMaker } from 'utils/sagas';
import { isCommentReply } from 'commentsResourceTypes';

const idsWithRepliesRelatedProps = [];

export function* watchForCommentsFetchedSuccessfully() {
  while(true) { // eslint-disable-line no-constant-condition
    const fetchSuccessAction = yield take(actions.COMMENTS_FETCH_SUCCESS);

    if(isCommentReply(fetchSuccessAction.resourceType)) {
      yield put(actions.commentUpdate(fetchSuccessAction.resourceId, null, {replies: fetchSuccessAction.result.commentsIds}));
      idsWithRepliesRelatedProps.push(fetchSuccessAction.resourceId);
    }
  }
}

export function* watchForCommentsReplying() {
  let previousCommentId = null;

  while(true) { // eslint-disable-line no-constant-condition
    const replyAction = yield take([
      actions.COMMENT_REPLY_BEGIN,
      actions.COMMENT_REPLY_CANCEL
    ]);

    if(previousCommentId) {
      yield put(actions.commentUpdate(previousCommentId, null, {isBeingRepliedTo: false}));
      yield put(actions.commentEmojisHide('secondLevel'));
      yield put(actions.commentReset('secondLevel'));

      previousCommentId = null;
    }

    if(replyAction.type == actions.COMMENT_REPLY_BEGIN) {
      let currentCommentId = replyAction.commentId;
      const comment = yield select(getCommentById, replyAction.commentId);

      if(isCommentReply(comment.resourceType)) {
        currentCommentId = comment.resourceId;
        const nestedComment = yield select(getCommentById, replyAction.commentId);

        yield put(actions.commentUpdateText({ text: `@${nestedComment.author.userName} `, key: 'secondLevel'}));
      }

      yield put(actions.commentUpdate(currentCommentId, null, {isBeingRepliedTo: true}));
      idsWithRepliesRelatedProps.push(currentCommentId);

      previousCommentId = currentCommentId;
    }
  }
}

export function* watchForResettingRepliesRelatedProperties() {
  while(true) { // eslint-disable-line no-constant-condition
    yield take(actions.COMMENTS_RESET_REPLIES_RELATED_PROPERTIES);

    for(let i = 0; i < idsWithRepliesRelatedProps.length; i++) {
      const id = idsWithRepliesRelatedProps[i];
      const action = actions.commentUpdate(id, null, {
        replies: null,
        isBeingRepliedTo: false
      });

      yield put(action);
    }
  }
}

export function* watchForHighlightedComments() {
  while(true) { // eslint-disable-line no-constant-condition
    const action = yield take(actions.COMMENT_UPDATE);

    if(action.updates.isHighlighted) {
      yield call(delay, 1000);

      const comment = yield select(getCommentById, action.commentId);
      yield put(actions.commentUpdate(action.commentId, null, { isHighlighted: false }));

      if(isCommentReply(comment.resourceType)) {
        yield put(actions.commentUpdate(comment.resourceId, null, { hasHighlightedReply: false }));
      }
    }
  }
}

const markAsUseful = optimisticUpdateMaker({
  idKey: 'commentId',
  localUpdateType: actions.COMMENT_UPDATE,
  errorType: actions.COMMENT_MARK_AS_USEFUL_ERROR,
  successType: actions.COMMENT_MARK_AS_USEFUL_SUCCESS,
  remoteUpdateActionCreator: function* (localUpdateAction) {
    if(localUpdateAction.updates && localUpdateAction.updates.useful !== undefined) {
      const comment = localUpdateAction.stateBeforeLocalUpdate;
      return actions.commentMarkAsUseful(localUpdateAction.commentId, comment.resourceType, localUpdateAction.updates.useful);
    }
  },
  localUpdateActionCreator: function* (lastRemoteState, errorAction) {
    return actions.commentUpdate(errorAction.commentId, null, lastRemoteState);
  }
});

export function* watchForSuccessfullyCreatedComment() {
  while(true) { // eslint-disable-line no-constant-condition
    const action = yield take(actions.COMMENT_CREATE_SUCCESS);

    yield put(actions.infoShow('success', action.successMessage, action.successTitle));
    yield put(actions.commentReplyCancel());
    yield put(actions.commentReset('firstLevel'));
  }
}

export default function* backgroundSaga(socket, request) {
  yield [
    fork(takeEvery, actions.COMMENTS_FETCH, request(api.commentsFetch, 'page', 'pageSize', 'commentsQuery')),
    fork(takeLatest, actions.COMMENT_CREATE, request(api.commentCreate, 'text', 'resourceType', 'resourceId')),
    fork(takeLatest, actions.COMMENT_MARK_AS_USEFUL, request(api.commentMarkAsUseful, 'commentId', 'resourceType', 'useful')),
    fork(watchForCommentsFetchedSuccessfully),
    fork(watchForCommentsReplying),
    fork(watchForResettingRepliesRelatedProperties),
    fork(markAsUseful),
    fork(watchForHighlightedComments),
    fork(watchForSuccessfullyCreatedComment)
  ];
}



/** WEBPACK FOOTER **
 ** ./src/members/state/comments/sagas.js
 **/